import { scopesAllowed, UserViewMode } from '@pubstack/common/src/rolesAndFeatures'
import { Scope } from '@pubstack/common/src/scope'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { forwardRef } from 'react'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import ProfilePopoverContent from '~/components/layout/header/ProfilePopoverContent'
import { ScopesButton } from '~/components/layout/header/ScopeButton'
import ScopesPopoverContent from '~/components/layout/header/ScopePopoverContent'
import { Popover } from '~/components/Popover'
import { PopoverButton } from '~/components/PopoverButton'
import { useKonamiCode } from '~/hooks/useKonamiCode'
import { currentScopeState, konamiUnlockedState, sidebarOpenState, upliftAppliedState } from '~/state'

const Header = forwardRef<HTMLButtonElement, { scopes: Scope[] }>(({ scopes }, menuToggleRef) => {
  const user = useAuthenticatedUser()
  const currentScope = useAtomValue(currentScopeState)
  const [sidebarOpen, setSidebarOpen] = useAtom(sidebarOpenState)
  const [konamiUnlocked, setKonamiUnlocked] = useAtom(konamiUnlockedState)
  const setUpliftApplied = useSetAtom(upliftAppliedState)

  // Pubstackers only. `isAdmin` is false while one of them browses as a publisher ("view as"), which is
  // the point of that mode: the egg is ours, not something to leave lying in a customer's header.
  const canSeeEasterEgg = user.isAdmin

  useKonamiCode(() => setKonamiUnlocked(true), { enabled: canSeeEasterEgg })

  const goToDocs = () => {
    window.open('https://docs.pubstack.io/', '_blank')
  }

  const hasMultiScope: boolean = scopesAllowed(user.userRoles) === 'all' || scopesAllowed(user.userRoles).length > 1

  const suffix = (view: UserViewMode | undefined) => {
    return view ? ` (as ${view})` : ''
  }

  return (
    <div className={'sticky top-0 flex h-16 items-center justify-between border-b border-platinum bg-white px-4'}>
      <div className={'flex flex-row items-center gap-2'}>
        {!sidebarOpen.pinned && (
          <Button
            ref={menuToggleRef}
            id={'HEADER_MENU_TOGGLE'}
            name={'HEADER_MENU_TOGGLE'}
            iconName={'logoMenu'}
            variant={'tertiary'}
            iconSize={'32px'}
            onClick={() => setSidebarOpen({ open: !sidebarOpen.open, pinned: false })}
            isHover={sidebarOpen.open || undefined}
            trackingLabel={sidebarOpen.open ? 'common-header-menu-display-unset' : 'common-header-menu-display-set'}
          />
        )}
        {hasMultiScope || !currentScope.id ? (
          <Popover trigger={<ScopesButton currentScope={currentScope} hasMultiScope={hasMultiScope || !currentScope.id} />}>{scopes && <ScopesPopoverContent scopes={scopes} />}</Popover>
        ) : (
          <ScopesButton currentScope={currentScope} hasMultiScope={hasMultiScope} />
        )}
      </div>
      <div className={'inline-flex items-center gap-6'}>
        {/* Easter egg, only ever on screen for a Pubstacker who typed the Konami code. The role is checked
            here as well as on the listener, so switching into "view as" takes the button back off screen. */}
        {canSeeEasterEgg && konamiUnlocked && (
          <div className={'flex items-center gap-2'}>
            <span className={'text-sm text-ink'}>{'Boost your revenue 🔥 👉'}</span>
            <Button id={'HEADER_UPLIFT'} variant={'primary'} onClick={() => setUpliftApplied(true)} trackingLabel={'common-header-uplift-apply'}>
              +30%
            </Button>
          </div>
        )}
        <Button variant={'tertiary'} id={'beamer'} trackingLabel={'common-header-whatsnew-view'}>
          What's new?
        </Button>
        <Button variant={'tertiary'} onClick={goToDocs} trackingLabel={'common-header-docs-navigate'}>
          Go to docs
        </Button>
        <Popover align={'end'} trigger={<PopoverButton label={`${user.profile.nickname}${suffix(user.view)}`} />}>
          <ProfilePopoverContent currentScope={currentScope} />
        </Popover>
      </div>
    </div>
  )
})

export default Header
